/**
 * -----------------------------------------------------------------------------
 * Popup - Create New FleetTag
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new fleettag in the organization.
 */

import { Formik, useFormik } from "formik";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCreateOrganizationFleettagMutation } from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { TModalsState, setModalsData } from "../../../api/store/commonSlice";
import { AdminFormFieldInput, AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { routeUrls } from "../../../navigation/routeUrls";
import { useLoggedInUserData } from "../../../utils/user";
import { formInitialValues, TFormFieldNames, YupValidationSchema } from "./validation";
import FTModal from "../../../components/common/FTModal";
import FormActionButtons from "../../../components/admin/formActionButtons";

const AdminsFleetTagsCreateNew = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation('translation', { keyPrefix: 'admins.fleettags.create_new' });
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");

  // fleettag mutation
  const [createOrgFleettagAPITrigger] = useCreateOrganizationFleettagMutation();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showCreateFleetTag: false }));
  };
    const formik = useFormik({
        initialValues:formInitialValues,
        validationSchema:YupValidationSchema,
        onSubmit:(values, { setSubmitting }) => {
          createOrgFleettagAPITrigger({
            accountid: `${thisUserOrganizationId}`,
            description: values.name,
            entityid: values.name.replace(/\s+/g, ''),
            entitytype: 900, // hardcoded value per backend
          })
            .unwrap()
            .then((data) => {
              if (!!data?.entityid) {
                dispatch(setModalsData({ ...modalsState, showCreateFleetTag: false }));
                toast.success(t('toast.create_success'), { autoClose: 10000 });
                navigate(`${routeUrls.dashboardChildren.adminChildren.fleettags}/${data?.entityid}`, { state: { new: true } });
              } else {
                toast.error(t('toast.create_failed'));
              }
            })
            .catch((error) => {
              const errors = !!error?.data ? serializeErrorKeyValues(error?.data) : [t('toast.create_failed')];
              toast.error(errors?.join(' '));
            })
            .finally(() => {
              setSubmitting(false);
            });
        }
      });
   const{
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        dirty,
        setFieldValue,
      } = formik;
        const invalidFields =
          Object.keys(errors)
            .filter(key => !!errors[key as TFormFieldNames]);
        const isFormValid = invalidFields.length === 0 && dirty;
        const MemoizedFormActionButtons = useMemo(() => {
          return (
            <>
              <FormActionButtons
                buttons={[
                  {
                    label:t('submit_button'),
                    variant:"primary",
                    disabled:!isFormValid || isSubmitting /* || isLoading*/
                  },
                  {
                    label:tMain('cancel'),
                    type:"button",
                    variant:"danger-transparent",
                    disabled:isSubmitting , /* || isLoading*/
                    onClick:()=>hideModal()
                  },
                ]}
              />
            </>
          );
        }, [
          isFormValid,
          isSubmitting,    
        ])

  return (
    <FTModal
      visible={modalsState.showCreateFleetTag}
      heading={t("heading")}
      onHide={hideModal}
    >
          <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
            <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
              {t("sub_heading")}
            </p>

            <AdminFormFieldInput
              label={t('name')}
              type="text"
              id="Name"
              name="name"
              placeholder={t('name_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              touched={touched.name}
              error={errors.name} />
            {MemoizedFormActionButtons}
            {/* <AdminFormFieldSubmit
              label={t('submit_button')}
              variant="primary"
              disabled={!isFormValid || isSubmitting } //|| isLoading
            />

            <AdminFormFieldSubmit
              label={tMain('cancel')}
              type="button"
              variant="danger-transparent"
              disabled={isSubmitting } // || isLoading
              onClick={hideModal}
            /> */}
          </form>
    </FTModal>
  )
};
export default AdminsFleetTagsCreateNew;